import { MLString, SystemType, TypeKind } from '../../types.js';
import { LazySourcedValue, ListSourcedValue, Option, OptionGroup, Source, SourcedValue } from '../../option.js';
import { SourceUtils } from '../../internal/SourceUtils.js';
import { DeepModelWalker } from '../../internal/ModelWalker.js';
import { pathToRoot } from '../../internal/PropertyPathWalker.js';
import { JsonPath } from '../../internal/JsonPath.js';
import { CompatibilityService } from '../../compatibility/CompatibilityService.js';
import { isOption, isRestrictedTo } from '../../compatibility/CompatibleValues.js';
import { createExpressionConstraint } from '../../constraint.js';
import { optionIdSymbol } from '../../engine-impl.js';
import { Product, ProductGroup } from '../../product.js';
import { CompatibilityTableParser } from '../../compatibility/CompatibilityTableParser.js';
export class ParsingDataFileVisitor {
    constructor(optionsRegistry, products, constraintRegistry, filterRegistry, externalParser, externalFieldRegistry, schema, compatibilityTables) {
        this.optionsRegistry = optionsRegistry;
        this.products = products;
        this.constraintRegistry = constraintRegistry;
        this.filterRegistry = filterRegistry;
        this.externalParser = externalParser;
        this.externalFieldRegistry = externalFieldRegistry;
        this.schema = schema;
        this.compatibilityTables = compatibilityTables;
    }
    visitCompatibilityTable(doc) {
        const parser = new CompatibilityTableParser(doc);
        const factory = parser.parse();
        const table = factory.createCompatibilityTable(this.schema, this.optionsRegistry);
        table.injectExternalFieldRegistry(this.externalFieldRegistry);
        this.compatibilityTables.set(factory.name, table);
    }
    visitExternalFiled(doc) {
        this.externalParser.parseField(doc);
        this.externalFieldRegistry.addExternalField(doc);
    }
    visitJsFilter(doc) {
        this.filterRegistry.addFilter(doc);
    }
    visitOclConstraint(doc) {
        this.constraintRegistry.addConstraint(doc);
    }
    visitOclFilter(doc) {
        this.filterRegistry.addFilter(doc);
    }
    visitOption(doc) {
        if (this.optionsRegistry.hasOption(doc._id)) {
            console.log(`Option id ${doc._id} already exists. Overwrite`);
        }
        this.optionsRegistry.registerOption(doc._id, doc.type, () => this.createOption(doc));
    }
    visitOptionGroup(doc) {
        if (this.optionsRegistry.hasOptionGroup(doc._id)) {
            console.log(`OptionGroup id ${doc._id} already exists. Overwrite`);
        }
        let x = {};
        x[optionIdSymbol] = doc._id;
        let optionGroupModel = Object.assign(x, doc.model);
        this.buildModel(optionGroupModel, doc.type);
        const optionsGroup = new OptionGroup(this.schema.getSchemaTypeReference(doc.type), doc.children.map((c) => this.optionsRegistry.getOptionOrOptionGroupById(c)), optionGroupModel, doc._id);
        this.optionsRegistry.addOptionGroup(doc._id, optionsGroup);
    }
    visitProduct(doc) {
        if (this.products[doc._id]) {
            console.log(`Product or ProductGroup id ${doc._id} already exists. Overwrite`);
        }
        this.products[doc._id] = this.createProduct(doc);
    }
    visitProductGroup(doc) {
        if (this.products[doc._id]) {
            console.log(`Product or ProductGroup id ${doc._id} already exists. Overwrite`);
        }
        this.products[doc._id] = this.createProductGroup(doc);
    }
    visitUnknown(doc) {
        if (doc.kind) {
            console.log('Doc kind is not defined : ' + doc.kind);
        }
        else {
            console.log('Invalid document', JSON.stringify(doc).slice(0, 15), '...');
        }
    }
    createProductGroup(productGroup) {
        SourceUtils.setProductGroupId(productGroup.model, productGroup._id);
        this.buildModel(productGroup.model, productGroup.type);
        return new ProductGroup(this.schema.getSchemaTypeReference(productGroup.type), productGroup.children.map(child => this.mapChildren(child)), productGroup.model, productGroup._id);
    }
    createProduct(product) {
        let model = product.model;
        this.buildModel(model, product.type);
        SourceUtils.setProductId(model, product._id);
        const registryConstraints = this.constraintRegistry.getConstraintsForProductType(product.type);
        let productConstraints = product.constraints ? product.constraints.map((e) => createExpressionConstraint(e, this.schema)) : [];
        const constraints = registryConstraints.concat(productConstraints);
        return new Product(this.schema.getSchemaTypeReference(product.type), model, constraints, product._id, this.filterRegistry);
    }
    createOption(doc) {
        this.buildModel(doc.model, doc.type);
        SourceUtils.setOptionId(doc.model, doc._id);
        return new Option(this.schema.getSchemaTypeReference(doc.type), doc.model, doc._id);
    }
    mapChildren(child) {
        if (!this.products[child]) {
            throw new Error(`Cannot find child id=${child}`);
        }
        return this.products[child];
    }
    buildModel(model, type) {
        let walker = new DeepModelWalker(this.schema, model, type, pValue => !pValue.property.opts.needSource);
        let propertyValue;
        while (propertyValue = walker.next()) {
            if (propertyValue.property.type.toLowerCase() === SystemType.MLString.toLowerCase()) {
                propertyValue.parent.value[propertyValue.property.name] = new MLString(propertyValue.value);
            }
            if (propertyValue.value === undefined) {
                propertyValue.parent.value[propertyValue.property.name] = propertyValue.property.opts.default;
            }
            if (propertyValue.property.opts.needSource) {
                if (!propertyValue.value) {
                    continue;
                }
                const propertyPath = pathToRoot(propertyValue, false);
                if (Array.isArray(propertyValue.property.opts.needSource)) {
                    propertyValue.parent.value[propertyValue.property.name] = this.createListSourcedValue(propertyValue.value);
                }
                else {
                    if (propertyValue.property.opts.needSource === 'specific' && this.schema.getType(propertyValue.property.type).kind == TypeKind.List) {
                        propertyValue.parent.value[propertyValue.property.name] = propertyValue.value.map(y => this.createSourcedValue(propertyValue.property.type, y, propertyPath));
                    }
                    else {
                        propertyValue.parent.value[propertyValue.property.name] = this.createSourcedValue(propertyValue.property.type, propertyValue.value, propertyPath);
                    }
                }
            }
        }
    }
    createSourcedValue(typeRef, obj, propertyPath) {
        let optionAvail = obj.optionAvailability ? obj.optionAvailability.map((e) => createExpressionConstraint(e, this.schema)) : [];
        const sourcesFn = () => {
            let sources = [];
            if ('match' in obj.source) {
                if (obj.source.match === '$type') {
                    sources = this.optionsRegistry.findOptionsWithType(typeRef);
                }
                else {
                    if ('compatibilityTables' in obj.source.match && Array.isArray(obj.source.match.compatibilityTables)) {
                        const tables = obj.source.match.compatibilityTables;
                        const tablesToUse = tables.map(t => [t, this.compatibilityTables.get(t)]);
                        const service = new CompatibilityService(this.schema);
                        tablesToUse.forEach(([tableName, table]) => service.addCompatibilityTable(table, tableName));
                        const values = service.getAvailableCompatibleValues(propertyPath);
                        if (isRestrictedTo(values)) {
                            for (let value of values.values) {
                                if (isOption(value)) {
                                    sources.push(value);
                                }
                            }
                        }
                        else {
                            sources = this.optionsRegistry.findOptionsWithType(typeRef);
                        }
                    }
                }
            }
            else if (Array.isArray(obj.source)) {
                sources = obj.source.map((x) => this.optionsRegistry.getOptionOrOptionGroupById(x));
            }
            else {
                throw new Error(`Invalid source for ${JSON.stringify(obj.value)}`);
            }
            return sources;
        };
        const values = Array.isArray(obj.value)
            ? obj.value.map((id) => this.optionsRegistry.getOptionById(id))
            : this.optionsRegistry.getOptionById(obj.value);
        if ('match' in obj.source && obj.source.match === '$type') {
            return new LazySourcedValue(values, this.optionsRegistry.findOptionsIdsWithType(typeRef), sourcesFn, optionAvail);
        }
        if (Array.isArray(obj.source)) {
            return new LazySourcedValue(values, obj.source, sourcesFn, optionAvail);
        }
        return new SourcedValue(values, sourcesFn(), optionAvail);
    }
    createListSourcedValue(obj) {
        let vals = [];
        for (let propVal of obj.value) {
            for (let src of Object.keys(obj.source)) {
                new JsonPath(propVal)
                    .update(src, oldValue => this.optionsRegistry.getOptionOrOptionGroupById(oldValue));
            }
            vals.push(propVal);
        }
        let srcs = {};
        for (let propSrc in obj.source) {
            srcs[propSrc] = new Source(obj.source[propSrc].source.map((x) => this.optionsRegistry.getOptionOrOptionGroupById(x)), obj.source[propSrc].optionAvailability ? obj.source[propSrc].optionAvailability.map((e) => createExpressionConstraint(e, this.schema)) : []);
        }
        return new ListSourcedValue(vals, srcs);
    }
}
//# sourceMappingURL=ParsingDataFileVisitor.js.map